import axios, {AxiosResponse} from 'axios'
import {Response, ID} from '@/_metronic/helpers'
import {
  SuppliersQueryResponse,
  SupplierOperationsQueryResponse,
  Supplier,
  SupplierProduct,
  RegistredProduct,
  SupplierOperation,
  OpProduct
} from './_models'

const SUPPLIER_URL = '/p2c/product/supplier'

const getSuppliers = (query: string): Promise<SuppliersQueryResponse> => {
  return axios
    .get(`${SUPPLIER_URL}?${query}`)
    .then((d: AxiosResponse<SuppliersQueryResponse>) => {
      return d.data
    })
}

const getSuppliersArray = (): Promise<Array<Supplier>> => {
  return axios
    .get(`${SUPPLIER_URL}?filter_status=STATUS_ENABLED`)
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

/**
 * Create a supplier
 */
const createSupplier = (supplier: Supplier) : Promise<Supplier | undefined> => {
  return axios
    .post(SUPPLIER_URL, supplier)
    .then((response: AxiosResponse<Response<Supplier>>) => response.data)
    .then((response: Response<Supplier>) => response.data)
}

/**
 * Update Supplier
 */
const updateSupplier = (supplier: Supplier): Promise<any> => {
  return axios
    .put(SUPPLIER_URL, supplier)
    .then((response: AxiosResponse<Response<any>>) => response.data)
}

/**
 * Get products having ${supplierId} availability
 */
const getRegistredProducts = (supplierId: ID) : Promise<Array<RegistredProduct>> => {
  return axios
    .get(`${SUPPLIER_URL}/products?supplier=${supplierId}`)
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const getSupplierOperation = (id: ID) : Promise<SupplierOperation | undefined> => {
  return axios
    .get(`${SUPPLIER_URL}/operation/${id}`)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

const deleteSupplierOperation = (id: ID) : Promise<void> => {
  return axios.delete(`${SUPPLIER_URL}/operation/${id}`)
}

const getSupplierOperationTypes = () : Promise<Array<string>> => {
  return axios
    .get(`${SUPPLIER_URL}/operation/types`)
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

const getSupplierOperations = (query: string): Promise<SupplierOperationsQueryResponse> => {
  return axios
    .get(`${SUPPLIER_URL}/operation?${query}`)
    .then((d: AxiosResponse<SupplierOperationsQueryResponse>) => {
      return d.data
    })
}

const createBulkOrder = (order: SupplierOperation): Promise<SupplierOperation | undefined> => {
  const data = {
    supplier: order.supplier,
    products: (order?.products || []).map((p: OpProduct) => {
      return {
        product: p.product,
        qty: p.qty
      }
    })
  }

  return axios
    .post(`${SUPPLIER_URL}/restock`, data)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

export const splitBulkOrder = (operationId: ID, products: ID[]): Promise<SupplierOperation | undefined> => {
  return axios
    .post(`${SUPPLIER_URL}/restock/split/${operationId}`, { products })
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

export const revertSplit = (operationId: ID): Promise<SupplierOperation | undefined> => {
  return axios
    .delete(`${SUPPLIER_URL}/restock/split/${operationId}`)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

const duplicateBulkOrder = (operationId: ID): Promise<SupplierOperation | undefined> => {
  return axios
    .post(`${SUPPLIER_URL}/restock/duplicate/${operationId}`)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

const updateBulkOrder = (order: SupplierOperation): Promise<SupplierOperation | undefined> => {
  const data = {
    operationId: order._id,
    products: (order?.products || []).map((p: OpProduct) => {
      return {
        product: p.product,
        qty: p.qty
      }
    })
  }

  return axios
    .put(`${SUPPLIER_URL}/restock`, data)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

const setBulkOrderState = (operationId: ID, state: string, date: Date, poNumber?: string, payRef?: string): Promise<SupplierOperation | undefined> => {
  return axios
    .put(`${SUPPLIER_URL}/restock/state`, {
      operationId,
      state,
      date,
      poNumber,
      payRef
    })
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

export const updateBulkOrderState = (operationId: ID, state: string, date: Date, poNumber?: string, payRef?: string): Promise<SupplierOperation | undefined> => {
  return axios
    .patch(`${SUPPLIER_URL}/restock/state`, {
      operationId,
      state,
      date,
      poNumber,
      payRef
    })
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}

const exportBulkOrder = (operationId: ID, format: string) : Promise<string> => {
  return axios
    .get(`${SUPPLIER_URL}/restock/export/${operationId}?format=${format}`)
    .then((response: AxiosResponse<string>) => response.data)
    .then((response: string) => response)
}

export const generateSupplyFile = (operationId: ID, format: string) : Promise<string> => {
  return axios
    .get(`${SUPPLIER_URL}/restock/supply-file/${operationId}?format=${format}`)
    .then((response: AxiosResponse<string>) => response.data)
    .then((response: string) => response)
}

const uploadVouchers = (data: FormData): Promise<any> =>  {
  return axios                                               
    .post(`${SUPPLIER_URL}/restock/vouchers`, data)
    .then((response: AxiosResponse<Response<SupplierOperation>>) => response.data)
    .then((response: Response<SupplierOperation>) => response.data)
}                                                            

/**
 * Lookup ${supplierId} product from it catalog
 */
const getSupplierProducts = (
  supplierId: ID,
  lookup: string,
  region: string | undefined,
  amount: number | undefined,
  currency: string | undefined
) : Promise<Array<SupplierProduct>> => {
  //Build query string
  const query = Object.entries({
    filter_supplier: supplierId,
    lookup: lookup,
    filter_region: region,
    filter_amount: amount,
    filter_currency: currency,
  })
    .map(([key, value]) => value ? `${key}=${value}` : undefined)
    .filter((v: string| undefined) => v !== undefined)
    .join('&')

  return axios
    .get(`${SUPPLIER_URL}/lookup?${query}`)
    .then((d: AxiosResponse<any>) => {
      return d.data.data
    })
}

export {
  getSuppliers,
  getSuppliersArray,
  createSupplier,
  updateSupplier,
  getSupplierProducts,
  getRegistredProducts,
  getSupplierOperation,
  deleteSupplierOperation,
  getSupplierOperations,
  getSupplierOperationTypes,
  createBulkOrder,
  duplicateBulkOrder,
  updateBulkOrder,
  exportBulkOrder,
  setBulkOrderState,
  uploadVouchers
}
